import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo } from 'react';
import type { DeferredRequestKey } from '~/constants/common/deferredRequests';
import type { DeepPartial } from '~/types';

export type ContextType = {
  // True when the server dispatched a deferred fetch, not that it resolved to a value:
  // a miss still reads true here and is recovered client-side via triggerFetchX.
  resolvedOnServer: Record<DeferredRequestKey, boolean>;
};

export const defaultContext: ContextType = {
  resolvedOnServer: {
    deliveryPickup: false,
    discounts: false,
    favorites: false,
    ownerReviews: false,
    pricing: false,
    trades: false,
    whyBuy: false
  }
};

const DeferredResolutionContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  resolvedOnServer?: ContextType['resolvedOnServer'];
}>;

export function DeferredResolutionContextProvider({
  children,
  resolvedOnServer
}: OwnProps) {
  const value = useMemo<ContextType>(
    () => ({
      resolvedOnServer: resolvedOnServer ?? defaultContext.resolvedOnServer
    }),
    [resolvedOnServer]
  );

  return (
    <DeferredResolutionContext.Provider value={value}>
      {children}
    </DeferredResolutionContext.Provider>
  );
}

DeferredResolutionContextProvider.displayName =
  'DeferredResolutionContextProvider';

export const generateDeferredResolutionContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedDeferredResolutionContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <DeferredResolutionContext.Provider
      value={generateDeferredResolutionContext(mockValue)}
    >
      {children}
    </DeferredResolutionContext.Provider>
  );
}

export function useResolvedOnServer() {
  return useContext(DeferredResolutionContext).resolvedOnServer;
}

export default DeferredResolutionContext;
